import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import type { IAnswerLength } from "@common/microservice-types/db/ai-models/answer-length";
import type { IResolvedUserSettings } from '@common/microservice-types/db/settings.user';
import type { IResolvedUserSkill, IUserSkill } from "@common/microservice-types/db/skill/skill.user";
import type { ContextAttachment, IR2Attachment, IR2ChatHistoryItem, IR2GetMessagesResponse, IR2MessageMeta, IR2SendMessageRequest, IR2SendMessageResponse, IR2ThreadDeleteResponse, IR2ThreadMsgDeleteResponse } from "@common/types/api/r2-types";
import { R2AttachmentType, R2MessageMetaType, R2SendMessageResult } from "@common/types/api/r2-types";
import type { IR2SkillUnwrapRequestV3, IR2SkillUnwrapResponseV3 } from "@common/types/api/skill-calls";
import { scanSkillTokens, serializeSkillInvocations } from "@common/utils/skill-tokens";
import { getLocalTimeWithTimezone } from "@common/utils/time";
import { CHAT_ACTION_NAME, CHAT_ATTACHMENT_LABEL, IChatActionList, IChatActionsData } from "@core/base/chat-action-base";
import { ChatSettingBase } from "@core/base/chat-setting-base";
import { SubscriptionBase } from "@core/base/subscription-base";
import { chatConstants } from '@core/constants/chat.constants';
import { IOfficeChatSetting } from "@core/office/office-chat-setting/office-chat-setting-v1/office-chat-setting-v1.types";
import { OfficeSessionService } from "@core/office/office-session/office.session.service";
import { OfficeService } from '@core/office/office-service/office-service';
import { OfficeGroupChat } from '@core/office/office-group-chat/office-group-chat';
import { OfficeDocumentContext, readOfficeDocumentContext } from '@core/office/office-document-context';
import type { IChatMessageResult, IGuid } from '@core/types/chat.types';
import { environment } from "@env";
import { TranslateService } from "@ngx-translate/core";
import { AiModelsService } from "@services/ai-models/ai-models.service";
import { CHAT_ERROR_NAME } from "@services/chat-error/chat-error.service";
import { logger } from "@services/logger/logger.service";
import { ThreadAttachmentStore } from "@services/r2-file/thread-attachment.store";
import { SessionCryptoService } from "@services/session-crypto/session-crypto.service";
import { UserSettingsApiService } from "@services/user-settings-api/user-settings-api.service";
import { InstructionsService } from "@services/instructions/instructions.service";
import { composeInstruction } from "@common/utils/instructions";
import { describeError } from "@utils/error";
import { checkAndCutByBytes } from '@utils/string';
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";
import { v4 as uuidV4 } from 'uuid';
import
{
    IChatOfficeMessage,
    IChatOfficeMessageBot,
    IChatOfficeMessageUser,
} from "./office-chat-v1.types";
import { OfficeDocumentThreadManager, PRIMARY_THREAD_SCOPE } from './office-document-thread-manager';
import { OfficeChatPendingAnswerStore } from './office.chat-pending-answer-store';

/**
 * State of one secondary thread of the current document — a conversation that
 * lives alongside the AI tab's own (primary) thread, e.g. the group chat's
 * summaries. Kept in memory only: unlike the primary thread these are not
 * mirrored into IndexedDB, they are re-read from the backend on each init.
 */
export interface IScopedThreadState
{
    threadId: string | null;
    lastAnswerMsgId: string | null;
    messages: BehaviorSubject<IChatOfficeMessage[]>;
    isSending: BehaviorSubject<boolean>;
}

/**
 * One request of an unwrapped nested-skill run, standing in for what the composer would
 * have built: the called skill's prompt, its model, and its name for the message badge.
 */
export interface IScaffoldedRequest
{
    text: string;
    model: string;
    meta?: IR2MessageMeta;
    imgModel?: string;
    useWebSearch?: boolean;
    contextMsgsCount?: number;
    skillHistoryEnabled?: boolean | null;
    queueStepIndex?: number;
    includeComposerAttachments?: boolean;
    instruction?: string;
    answerLength?: IAnswerLength;
    accountSettings?: IResolvedUserSettings;
}

interface IQueueInstructionContext
{
    session: string;
    threadId: string | null;
    lastAnswerMsgId: string | null;
    documentId: string | null;
}

interface IFailedInstructionSend
{
    context: string;
    request: IR2SendMessageRequest;
    meta?: IR2MessageMeta;
    attachments: IR2Attachment[];
    userText: string;
    localUserMessageId: string | null;
}

/** How far a nested-skill run has got, or null when no run is going. */
export interface ISkillQueueProgress
{
    done: number;
    total: number;
}

export class OfficeChatV1
{
    /** Temporary switch for the chat composer. The planner remains available for later re-enable. */
    public inlineSkillsEnabled = false;
    // The backend may persist the assistant answer slightly after msg/send
    // resolves, so we poll msgs/get until the new message shows up.
    private static readonly ANSWER_POLL_INTERVAL_MS = 1_200;
    private static readonly ANSWER_POLL_TIMEOUT_MS = 6.5 * 60 * 1_000;

    public $messages: BehaviorSubject<IChatOfficeMessage[]> = new BehaviorSubject<IChatOfficeMessage[]>([]);

    // The new backend keeps conversation history server-side per thread. The
    // first send creates a thread; subsequent sends reuse this id for context.
    private threadId: string | null = null;
    private readonly threadIdState = new BehaviorSubject<string | null>(null);

    // Newest assistant message we have already shown. Used as the baseline so
    // polling can tell a freshly generated answer from the previous one.
    private lastAnswerMsgId: string | null = null;

    // scope -> state of the document's secondary threads. Empty until a caller
    // asks for one (see getScopedThreadState).
    private scopedThreads = new Map<string, IScopedThreadState>();

    protected skillQueueProgress = new BehaviorSubject<ISkillQueueProgress | null>(null);

    // True while we are waiting for an assistant answer to a message the backend
    // has already accepted — both for a live send and for a send resumed after
    // the task pane was reopened (see PendingAnswerStore / resumePendingAnswer).
    protected awaitingAnswer = new BehaviorSubject<boolean>(false);

    protected pendingAnswerStore = new OfficeChatPendingAnswerStore();
    private failedInstructionSend: IFailedInstructionSend | null = null;
    private lastAttemptLocalMessageId: string | null = null;

    constructor(
        protected httpClient: HttpClient,
        protected settingService: ChatSettingBase,
        protected sessionService: OfficeSessionService,
        protected aiModelsService: AiModelsService,
        protected subscriptionService: SubscriptionBase,
        protected attachmentStore: ThreadAttachmentStore,
        protected userSettingsApi: UserSettingsApiService,
        protected sessionCrypto: SessionCryptoService,
        protected threadManager: OfficeDocumentThreadManager,
        protected translate: TranslateService,
        protected instructions: InstructionsService,
        protected documentOffice: OfficeService,
        protected groupChat?: OfficeGroupChat,
    )
    {
        this.loadModelsOnAuth();
    }

    protected get(): Observable<IChatOfficeMessage[]>
    {
        return this.$messages.asObservable();
    }

    public $isAwaitingAnswer(): Observable<boolean>
    {
        return this.awaitingAnswer.asObservable();
    }

    protected getCurrentThreadId(): string | null
    {
        return this.threadId;
    }

    public currentThreadId$(): Observable<string | null> { return this.threadIdState.asObservable(); }

    public setHistoryForCurrentThread(enabled: boolean): Promise<boolean>
    {
        return this.userSettingsApi.setHistoryEnabled(enabled);
    }

    protected setCurrentThreadId(threadId: string | null): void
    {
        this.threadId = threadId;
        this.threadIdState.next(threadId);
    }

    protected setLastAnswerMessageId(messageId: string | null): void
    {
        this.lastAnswerMsgId = messageId;
    }

    // Models and user settings require an authenticated MS session — prefetch once auth succeeds.
    private loadModelsOnAuth(): void
    {
        this.sessionService.authState$.subscribe(async (isAuth) =>
        {
            if (!isAuth) { return; }
            const sessionInfo = this.sessionService.getSession();
            if (sessionInfo?.session_id)
            {
                await this.aiModelsService.loadModels();
                void this.userSettingsApi.getSettings();
                void this.userSettingsApi.getAnswerLengths();
            }
        });
    }

    protected async send(
        message: string,
        dataAction: IChatActionsData | null,
        isSaveUserMessage: boolean = true,
        actions: IChatActionList,
        retry = false,
    ): Promise<IChatMessageResult>
    {
        const failed = this.failedInstructionSend;
        const replaceUserMessageId = retry ? (failed ? failed.localUserMessageId : this.lastAttemptLocalMessageId) : null;
        if (retry && failed?.context === this.instructionContext())
        {
            this.failedInstructionSend = null;
            this.lastAttemptLocalMessageId = null;
            return this.sendOne(message, dataAction, isSaveUserMessage, actions, undefined, failed, undefined, replaceUserMessageId);
        }
        this.failedInstructionSend = null;
        this.lastAttemptLocalMessageId = null;
        const context = this.instructionContext();
        let accountSettings: IResolvedUserSettings;
        try
        {
            accountSettings = await this.userSettingsApi.waitForConfirmedSettings();
            this.assertInstructionContext(context);
        }
        catch (error)
        {
            return { text: this.parseError(error), isError: true, traceID: uuidV4() as IGuid,
                timestamp: Date.now(), planningAccepted: false };
        }
        const selectedSkill = this.settingService.getCurrentSkill();
        if (!this.inlineSkillsEnabled && selectedSkill)
        {
            const escapedInput = message.replace(/(\\*)\[skill:/g, (_match, slashes: string) =>
                `${ slashes }${ slashes.length % 2 === 0 ? '\\' : '' }[skill:`);
            const source = serializeSkillInvocations([ { skillId: selectedSkill.id, inputText: escapedInput } ]);
            return this.runSkillQueue(source, dataAction, isSaveUserMessage, actions, replaceUserMessageId, message, accountSettings);
        }
        if (this.inlineSkillsEnabled && this.hasSkillTokens(message))
        {
            return this.runSkillQueue(message, dataAction, isSaveUserMessage, actions, replaceUserMessageId, null, accountSettings);
        }

        return this.sendOne(message, dataAction, isSaveUserMessage, actions, undefined, undefined, undefined, replaceUserMessageId, accountSettings);
    }

    private async runSkillQueue(
        sourceText: string,
        dataAction: IChatActionsData | null,
        isSaveUserMessage: boolean,
        actions: IChatActionList,
        replaceUserMessageId: string | null = null,
        selectedInput: string | null = null,
        accountSettings?: IResolvedUserSettings,
    ): Promise<IChatMessageResult>
    {
        const context = this.instructionContext();
        const inheritedDocumentContext = actions?.attachDocument?.value ?? !!dataAction?.attachDocument;
        const inheritedDocumentChat = actions?.documentChat?.value ?? !!dataAction?.documentChat;
        const documentId = await this.currentDocumentOwner();
        let instruction: string;
        let answerLengths: IAnswerLength[];
        let unwrapped: IR2SkillUnwrapResponseV3;
        try
        {
            unwrapped = await this.unwrapSkillCalls(sourceText, accountSettings);
            this.assertInstructionContext(context);
            instruction = await this.instructions.forSend(null, accountSettings);
            this.assertInstructionContext(context);
            answerLengths = await this.userSettingsApi.getAnswerLengths();
            this.assertInstructionContext(context);
        }
        catch (e)
        {
            logger.log('Failed to plan skill invocation:', e);
            return {
                text: this.parseError(e),
                isError: true,
                traceID: uuidV4() as IGuid,
                timestamp: Date.now(),
                planningAccepted: false,
            };
        }

        if (unwrapped.items.length === 0)
        {
            return {
                text: 'Unable to plan skill invocation',
                isError: true,
                traceID: uuidV4() as IGuid,
                timestamp: Date.now(),
                planningAccepted: false,
            };
        }

        const { invocation, items, warnings } = unwrapped;
        if (warnings.length > 0)
        {
            logger.log('Skill plan warnings:', warnings);
        }
        const run: IQueueInstructionContext = {
            session: this.instructionSession(), threadId: this.threadId, lastAnswerMsgId: this.lastAnswerMsgId,
            documentId,
        };
        let prepared: Array<{ scaffold: IScaffoldedRequest; snapshot: IFailedInstructionSend }>;
        try
        {
            prepared = [];
            for (const [ index, item ] of items.entries())
            {
                const temporarySource = index === 0 && !!(dataAction?.selection || dataAction?.clipboard || dataAction?.img);
                const oneTimeDocumentChat = index === 0 && !!dataAction?.oneTimeDocumentChat;
                let documentEnabled = item.settings?.documentContextEnabled
                    ?? (accountSettings ? !!accountSettings.documentContextEnabled : inheritedDocumentContext);
                let documentChatEnabled = item.settings?.documentChatEnabled
                    ?? (accountSettings ? !!accountSettings.documentChatEnabled : inheritedDocumentChat);
                if (oneTimeDocumentChat && item.settings?.documentContextEnabled !== true
                    && item.settings?.documentChatEnabled !== false)
                {
                    documentEnabled = false;
                    documentChatEnabled = true;
                }
                if (item.settings?.documentContextEnabled === true) documentChatEnabled = false;
                if (item.settings?.documentChatEnabled === true) documentEnabled = false;
                if (temporarySource && !oneTimeDocumentChat
                    && item.settings?.documentContextEnabled !== true && item.settings?.documentChatEnabled !== true)
                {
                    documentEnabled = false;
                    documentChatEnabled = false;
                }
                const document = await this.prepareDocumentContext(
                    accountSettings ? null : index === 0 ? dataAction : null, documentEnabled);
                const documentChat = await this.prepareDocumentChatContext(documentChatEnabled,
                    oneTimeDocumentChat ? dataAction?.documentChat : undefined);
                this.assertInstructionContext(context);
                if (documentId !== await this.currentDocumentOwner()) throw new Error('Document context changed');
                const scaffold: IScaffoldedRequest = {
                    text: item.text, model: item.model, meta: { ...item.meta, original_text: item.meta?.original_text ?? '' },
                    imgModel: item.img_model, useWebSearch: item.use_web_search, contextMsgsCount: item.context_msgs_count,
                    skillHistoryEnabled: item.settings?.historyEnabled, queueStepIndex: index,
                    instruction: item.settings?.instruction ?? instruction,
                    answerLength: answerLengths.find((length) => length.id === item.settings?.answerLengthId),
                    accountSettings,
                };
                if (index === 0)
                {
                    scaffold.meta = {
                        ...scaffold.meta,
                        original_text: selectedInput ?? invocation.roots[ 0 ]?.input_text ?? '',
                        ...(selectedInput === null ? { skill_invocation_source: invocation.source_text } : {}),
                        skill_invocations: invocation.roots.map(({ skill_id, skill_title, input_text }) => ({
                            skill_id,
                            skill_title,
                            input_text,
                        })),
                    };
                }
                const itemData: IChatActionsData | null = (index === 0 && dataAction) || documentEnabled || documentChatEnabled ? {
                    selection: '', clipboard: '', history: [], img: false,
                    ...(index === 0 ? dataAction : null),
                    ...(documentEnabled ? document : { attachDocument: '', documentAttachments: [] }),
                    documentChat,
                } : null;
                prepared.push({ scaffold, snapshot: { context, localUserMessageId: null,
                    userText: scaffold.meta?.skill_invocation_source ?? scaffold.meta?.original_text ?? '',
                    ...this.buildSendRequest(scaffold.text, itemData, actions, scaffold, scaffold.instruction, null, index === 0) } });
            }
        }
        catch (error)
        {
            return { text: this.parseError(error), isError: true, traceID: uuidV4() as IGuid, timestamp: Date.now(), planningAccepted: false };
        }
        let result: IChatMessageResult | null = null;

        try
        {
            for (let index = 0; index < items.length; index++)
            {
                if (run.session !== this.instructionSession()) return {
                    text: 'Instruction context changed', isError: true, traceID: uuidV4() as IGuid, timestamp: Date.now(),
                };
                if (run.documentId !== await this.currentDocumentOwner()) return {
                    text: 'Document context changed', isError: true, traceID: uuidV4() as IGuid, timestamp: Date.now(),
                };
                this.skillQueueProgress.next({ done: index, total: items.length });
                const { scaffold, snapshot } = prepared[index];
                result = await this.sendOne(
                    scaffold.text,
                    index === 0 ? dataAction : null,
                    isSaveUserMessage,
                    actions,
                    scaffold,
                    snapshot,
                    run,
                    index === 0 ? replaceUserMessageId : null,
                );

                if (result.isError)
                {
                    return { ...result, planningAccepted: true };
                }
            }
        }
        finally
        {
            this.skillQueueProgress.next(null);
        }

        return result ? { ...result, planningAccepted: true } : {
            text: '',
            isError: false,
            traceID: uuidV4() as IGuid,
            timestamp: Date.now(),
            planningAccepted: true,
        };
    }

    private hasSkillTokens(value: string): boolean
    {
        try
        {
            return scanSkillTokens(value).some(part => part.kind === 'skill');
        }
        catch
        {
            return true;
        }
    }

    private unwrapSkillCalls(sourceText: string, accountSettings?: IResolvedUserSettings): Promise<IR2SkillUnwrapResponseV3>
    {
        const setting = this.settingService.getSetting();
        const settingOriginal = this.settingService.getSettingOriginal<IOfficeChatSetting>();
        const body: IR2SkillUnwrapRequestV3 = {
            text: sourceText,
            model: accountSettings?.resolvedChatModel?.apiName ?? setting.options?.model ?? settingOriginal.model,
            locale: (this.translate.currentLang || 'en').toLowerCase().split('-')[ 0 ],
            environment: this.userSettingsApi.environmentId(),
        };

        // skills-microservice endpoint (SKILLS_MICROSERVICE_HOST) - not fronted by
        // the AddinSecureTransportController/SECURE_API_HOST gateway, unlike the
        // AI_API_HOST-backed calls elsewhere in this class. It uses the plain add-in
        // v3 proxy, the same transport family as skills/quick-actions/settings.
        return this.sessionCrypto.postAddinPlain<IR2SkillUnwrapRequestV3, IR2SkillUnwrapResponseV3>(
            '/v3/skills/unwrap',
            body,
        );
    }

    private async sendOne(
        message: string,
        dataAction: IChatActionsData | null,
        isSaveUserMessage: boolean = true,
        actions: IChatActionList,
        scaffold?: IScaffoldedRequest,
        replay?: IFailedInstructionSend,
        run?: IQueueInstructionContext,
        replaceUserMessageId: string | null = null,
        accountSettings?: IResolvedUserSettings,
    ): Promise<IChatMessageResult>
    {
        let request: IR2SendMessageRequest;
        let meta: IR2MessageMeta | undefined;
        let attachments: IR2Attachment[];
        let context = this.instructionContext();
        let documentOwner: string | null;

        try
        {
            documentOwner = run?.documentId ?? await this.currentDocumentOwner();
            this.assertInstructionContext(context);
            if (replay)
            {
                ({ request, meta, attachments } = replay);
                if (run) request = { ...request, thread_id: run.threadId ?? undefined };
            }
            else
            {
                const skill = this.settingService.getCurrentSkill();
                const confirmed = scaffold?.accountSettings ?? accountSettings;
                const oneTimeDocumentChat = !!dataAction?.oneTimeDocumentChat;
                const temporarySource = !!(dataAction?.selection || dataAction?.clipboard || dataAction?.img);
                let documentEnabled = skill?.documentContextEnabled
                    ?? (confirmed ? !!confirmed.documentContextEnabled : actions?.attachDocument?.value ?? !!dataAction?.attachDocument);
                let documentChatEnabled = skill?.documentChatEnabled
                    ?? (oneTimeDocumentChat || (confirmed ? !!confirmed.documentChatEnabled : actions?.documentChat?.value ?? !!dataAction?.documentChat));
                if (skill?.documentContextEnabled === true) documentChatEnabled = false;
                if (skill?.documentChatEnabled === true || oneTimeDocumentChat) documentEnabled = false;
                if (confirmed && temporarySource && !oneTimeDocumentChat
                    && skill?.documentContextEnabled !== true && skill?.documentChatEnabled !== true)
                {
                    documentEnabled = false;
                    documentChatEnabled = false;
                }
                const instruction = scaffold?.instruction ?? await this.instructions.forSend(skill, confirmed);
                this.assertInstructionContext(context);
                const document = await this.prepareDocumentContext(confirmed ? null : dataAction, documentEnabled);
                const documentChat = await this.prepareDocumentChatContext(documentChatEnabled,
                    oneTimeDocumentChat || !confirmed ? dataAction?.documentChat : undefined);
                this.assertInstructionContext(context);
                if (documentOwner !== await this.currentDocumentOwner()) throw new Error('Document context changed');
                if (dataAction || documentEnabled || documentChatEnabled)
                    dataAction = { selection: '', clipboard: '', history: [], img: false,
                        ...dataAction, ...document, documentChat };
                ({ request, meta, attachments } = this.buildSendRequest(message, dataAction, actions, scaffold, instruction, skill, true, accountSettings));
            }
            await this.attachmentStore.validateSendAttachments(attachments);
            this.assertInstructionContext(context);
        }
        catch (e)
        {
            return {
                text: this.parseError(e),
                isError: true,
                traceID: uuidV4() as IGuid,
                timestamp: Date.now(),
            };
        }

        logger.log('Send request:', request);

        const localUserMsgId = uuidV4();
        const messageUser: IChatOfficeMessageUser = {
            sender: chatConstants.MESSAGE_SENDER.user,
            value: replay?.userText ?? (scaffold
                ? (scaffold.meta?.skill_invocation_source ?? scaffold.meta?.original_text ?? '')
                : message),
            meta,
            attachments,
            createdAt: Date.now(),
            msg_id: localUserMsgId,
            localId: localUserMsgId,
            isPending: true,
        };

        const isVisible = () => run
            ? run.session === this.instructionSession() && run.threadId === this.threadId
            : context === this.instructionContext();
        const timestamp = Date.now();
        const traceID = uuidV4() as IGuid;

        // Thread id whose pending-answer marker we own for this send, so the
        // finally block clears exactly what the try block wrote.
        let pendingThreadId: string | null = null;
        let writtenLocalMessageId: string | null = null;
        const instructionContext = this.instructionContext();

        try
        {
            if (run)
            {
                if (run.threadId)
                {
                    const history = await this.fetchThreadHistory(run.threadId);
                    const previous = history.find((item) => item.role === 'ASSISTANT' && (!!item.content?.text || !!item.attachments?.length));
                    run.lastAnswerMsgId = previous?.msg_id ?? run.lastAnswerMsgId;
                }
                if (run.session !== this.instructionSession()) throw new Error('Instruction context changed');
            }
            else
            {
                await this.refreshLastAnswerMsgId();
                this.assertInstructionContext(context);
            }
            if (documentOwner !== await this.currentDocumentOwner()) throw new Error('Document context changed');

            if (isSaveUserMessage && isVisible())
            {
                if (replaceUserMessageId)
                {
                    const messages = this.$messages.getValue();
                    const index = messages.findIndex(item => item.sender === chatConstants.MESSAGE_SENDER.user
                        && item.localId === replaceUserMessageId);
                    if (index !== -1)
                    {
                        this.$messages.next([ ...messages.slice(0, index), ...messages.slice(index + 1) ]);
                    }
                }
                this.write(messageUser);
                writtenLocalMessageId = localUserMsgId;
                this.lastAttemptLocalMessageId = localUserMsgId;
            }

            const sendRes = await this.sessionCrypto.postAddin<IR2SendMessageRequest, IR2SendMessageResponse>(
                '/msg/send',
                request,
                { headers: { 'x-hs-request-id': traceID } },
            );

            if (sendRes.result !== 0)
            {
                throw new Error(
                    this.mapApiResultToErrorName(sendRes.details?.result)
                    ?? 'Failed to send message'
                );
            }

            const responseThreadId = sendRes.thread_id ?? run?.threadId ?? this.threadId;
            if (!responseThreadId) throw new Error('No thread id returned');
            const visible = isVisible();
            if (run) run.threadId = responseThreadId;
            if (visible)
            {
                if (this.threadId && responseThreadId !== this.threadId)
                    console.error(`Thread id mismatch: current=${this.threadId}, response=${responseThreadId}`);
                else if (this.threadId !== responseThreadId) this.setCurrentThreadId(responseThreadId);
                if (!run) context = this.instructionContext();
                this.attachmentStore.clear();
            }
            const baseline = run ? run.lastAnswerMsgId : this.lastAnswerMsgId;
            const answerThreadId = !run && visible ? this.threadId ?? responseThreadId : responseThreadId;
            pendingThreadId = answerThreadId;
            this.pendingAnswerStore.write({ threadId: answerThreadId, baselineAnswerMsgId: baseline, createdAt: Date.now() });
            const answer = await this.fetchLatestAnswer(answerThreadId, baseline);
            if (run) run.lastAnswerMsgId = answer.msg_id;
            if (isVisible()) this.lastAnswerMsgId = answer.msg_id;
            if (answer.userMsgId)
            {
                messageUser.msg_id = answer.userMsgId;
                messageUser.isPending = false;
            }
            const botMessage = this.createBotMessage(
                answer?.text || '',
                !!request.img_model,
                answer.attachments,
                answer.msg_id,
                answer.item,
            );
            if (isVisible()) this.write(botMessage);

            return {
                text: answer.text,
                isError: false,
                traceID,
                timestamp,
            };
        }
        catch (e)
        {
            if (!pendingThreadId && isVisible())
                this.failedInstructionSend = { context: instructionContext, request, meta, attachments, userText: messageUser.value, localUserMessageId: writtenLocalMessageId };
            return {
                text: this.parseError(e),
                isError: true,
                traceID,
                timestamp,
            };
        }
        finally
        {
            if (pendingThreadId)
            {
                this.pendingAnswerStore.clear(pendingThreadId);
            }
        }
    }

    private async refreshLastAnswerMsgId(): Promise<void>
    {
        if (!this.threadId)
        {
            this.lastAnswerMsgId = null;
            return;
        }

        const res = await firstValueFrom<IR2GetMessagesResponse>(
            this.httpClient.post<IR2GetMessagesResponse>(
                `${ environment.office_api_host }/api/r2-chat/msgs/get`,
                { thread_id: this.threadId, page_size: 50, msg_order: 'desc' },
            ),
        );

        const assistant = (res.chat_history ?? []).find(m =>
            m.role === 'ASSISTANT'
            && (!!m.content?.text || (m.attachments?.length ?? 0) > 0)
        );

        if (assistant?.msg_id)
        {
            this.lastAnswerMsgId = assistant.msg_id;
        }
    }

    // After msg/send, wait for the new answer to appear via msgs/get (same
    // fetch-after-send flow as r2-chat-web). Polls until an assistant message
    // newer than the previous one shows up, so we never read a stale answer.
    protected async fetchLatestAnswer(threadId: string, previousAnswerMsgId: string | null): Promise<{ text: string; attachments: IR2Attachment[]; msg_id: string; userMsgId?: string; item: IR2ChatHistoryItem; }>
    {
        const deadline = Date.now() + OfficeChatV1.ANSWER_POLL_TIMEOUT_MS;

        do
        {
            const history = await this.fetchThreadHistory(threadId);
            const assistant = history.find(m =>
                m.role === 'ASSISTANT'
                && (!!m.content?.text || (m.attachments?.length ?? 0) > 0)
            );

            if (assistant && assistant.msg_id !== previousAnswerMsgId)
            {
                // Newest USER entry in this same poll is the message we just sent
                // (sends are sequential/awaited), so its msg_id is the real backend id.
                const userEntry = history.find(m => m.role === 'USER');
                return {
                    text: assistant.content.text,
                    msg_id: assistant.msg_id,
                    attachments: assistant.attachments ?? [],
                    userMsgId: userEntry?.msg_id,
                    item: assistant,
                };
            }

            await this.delay(OfficeChatV1.ANSWER_POLL_INTERVAL_MS);
        }
        while (Date.now() < deadline);

        throw new Error('No answer received');
    }

    /** One page of a thread's history, newest first. */
    protected async fetchThreadHistory(threadId: string): Promise<IR2ChatHistoryItem[]>
    {
        try
        {
            const res = await this.sessionCrypto.postAddin<
                { thread_id: string; page_size: number; msg_order: 'desc'; },
                IR2GetMessagesResponse
            >(
                '/msgs/get',
                { thread_id: threadId, page_size: 50, msg_order: 'desc' },
            );

            return res.chat_history ?? [];
        }
        catch (error)
        {
            // Unlike msg/send, the documented -6 result for msgs/get is service
            // maintenance. Keep this at the endpoint boundary: -6 has different
            // meanings on other R2 endpoints.
            if (this.extractApiResult(error) === -6)
            {
                throw new Error(CHAT_ERROR_NAME.SERVICE_MAINTENANCE);
            }

            throw error;
        }
    }

    // --- Secondary threads -------------------------------------------------
    // The same document can carry more than one conversation. The primary one
    // backs the AI tab (message store, IndexedDB mirror, quick actions, ...);
    // the ones below are plain request/answer threads addressed by scope.

    protected getScopedThreadState(scope: string): IScopedThreadState
    {
        const existing = this.scopedThreads.get(scope);
        if (existing)
        {
            return existing;
        }

        const state: IScopedThreadState = {
            threadId: null,
            lastAnswerMsgId: null,
            messages: new BehaviorSubject<IChatOfficeMessage[]>([]),
            isSending: new BehaviorSubject<boolean>(false),
        };
        this.scopedThreads.set(scope, state);
        return state;
    }

    /** Scopes of every secondary thread a caller has opened so far. */
    protected getScopedThreadScopes(): string[]
    {
        return Array.from(this.scopedThreads.keys());
    }

    protected async ensureScopedThread(scope: string, forceRefresh = false): Promise<string>
    {
        if (scope === PRIMARY_THREAD_SCOPE)
        {
            return this.ensureCurrentThread(forceRefresh);
        }

        const state = this.getScopedThreadState(scope);
        if (state.threadId && !forceRefresh)
        {
            return state.threadId;
        }

        const context = await this.threadManager.ensureThread(forceRefresh, scope);
        state.threadId = context.threadId;
        return context.threadId;
    }

    /**
     * Send one message to a secondary thread and wait for its answer. Nothing
     * from the composer (quick action, skill, attachments, host context) takes
     * part — these threads are driven by callers that build their own prompt.
     */
    protected async sendToScopedThread(scope: string, text: string): Promise<IR2ChatHistoryItem>
    {
        const context = this.instructionContext();
        const accountSettings = await this.userSettingsApi.waitForConfirmedSettings();
        this.assertInstructionContext(context);
        const state = this.getScopedThreadState(scope);
        const threadId = await this.ensureScopedThread(scope);
        const instruction = await this.instructions.forSend(null, accountSettings);
        this.assertInstructionContext(context);
        const setting = this.settingService.getSetting();

        const request: IR2SendMessageRequest = {
            copilot_id: this.subscriptionService.getActiveCopilot()?.id ?? '',
            text,
            model: accountSettings?.resolvedChatModel?.apiName ?? this.resolveScopedModelApiName(),
            instruction: composeInstruction(accountSettings?.answerLength?.instruction ?? setting.options?.answerLengthInstruction, instruction),
            ...((accountSettings?.answerLength?.outputTokenLimit ?? setting.options?.answerLengthOutputLimit) !== undefined
                ? { max_bytes_output: accountSettings?.answerLength?.outputTokenLimit ?? setting.options?.answerLengthOutputLimit }
                : {}),
            thread_id: threadId,
            environment: {
                time: getLocalTimeWithTimezone(),
            },
        };

        const sendRes = await this.sessionCrypto.postAddin<IR2SendMessageRequest, IR2SendMessageResponse>(
            '/msg/send',
            request,
        );

        if (sendRes.result !== R2SendMessageResult.Success)
        {
            throw new Error(
                this.mapApiResultToErrorName(sendRes.details?.result)
                ?? 'Failed to send message'
            );
        }

        state.threadId = sendRes.thread_id ?? threadId;

        const answer = await this.fetchLatestAnswer(state.threadId, state.lastAnswerMsgId);
        state.lastAnswerMsgId = answer.msg_id;
        return answer.item;
    }

    private instructionSession(): string
    {
        const session = this.sessionService.getSession();
        return `${session?.user_id ?? ''}:${session?.session_id ?? ''}`;
    }

    private instructionContext(): string
    {
        return `${this.sessionService.getSession()?.user_id ?? ''}:${this.threadId ?? ''}`;
    }

    private assertInstructionContext(context: string): void
    {
        if (context !== this.instructionContext()) throw new Error('Instruction context changed');
    }

    private resolveScopedModelApiName(): string
    {
        const setting = this.settingService.getSetting();
        const settingOriginal = this.settingService.getSettingOriginal<IOfficeChatSetting>();
        const model = setting.options?.model
            || settingOriginal.model
            || this.aiModelsService.getDefaultModelForFeature('CHAT')?.apiName;

        if (!model)
        {
            throw new Error(CHAT_ERROR_NAME.MODEL_NOT_FOUND);
        }

        return model;
    }

    protected async clearCurrentThread(): Promise<void>
    {
        const threadId = this.threadId;
        this.setCurrentThreadId(null);
        this.lastAnswerMsgId = null;
        this.threadManager.reset();
        this.attachmentStore.clear();

        if (!threadId)
        {
            return;
        }

        const copilotId = this.subscriptionService.getActiveCopilot()?.id;

        try
        {
            const response = await this.sessionCrypto.postAddin<
                {
                    thread_ids: string[];
                    copilot_id?: string;
                },
                IR2ThreadDeleteResponse
            >(
                '/thread/delete',
                {
                    thread_ids: [ threadId ],
                    ...(copilotId ? { copilot_id: copilotId } : {}),
                },
            );

            if (response.result !== 0)
            {
                logger.warn('clearCurrentThread: thread delete failed', response);
            }
        }
        catch (e)
        {
            logger.error('clearCurrentThread: thread delete failed', e);
        }
    }

    // Delete a single message from the current thread on the backend. Mirrors
    // clearCurrentThread: best-effort and never blocks the local (IndexedDB)
    // deletion, so the UI stays consistent even if the network call fails. Any
    // file attachments on the message are deleted alongside it on the API side.
    protected async deleteMessageRemote(message: IChatOfficeMessage): Promise<void>
    {
        const threadId = this.threadId;
        if (!threadId || !message.msg_id)
        {
            return;
        }

        const fileIds = this.collectFileAttachmentIds(message);
        const copilotId = this.subscriptionService.getActiveCopilot()?.id;

        try
        {
            const response = await this.sessionCrypto.postAddin<
                {
                    thread_id: string;
                    msg_id: string;
                    copilot_id?: string;
                    file_ids?: string[];
                },
                IR2ThreadMsgDeleteResponse
            >(
                '/thread/msg/delete',
                {
                    thread_id: threadId,
                    msg_id: message.msg_id,
                    ...(copilotId ? { copilot_id: copilotId } : {}),
                    ...(fileIds.length > 0 ? { file_ids: fileIds } : {}),
                },
            );

            if (response.result !== 0)
            {
                logger.warn('deleteMessageRemote: message delete failed', response);
            }
        }
        catch (e)
        {
            logger.error('deleteMessageRemote: message delete failed', e);
        }
    }

    private collectFileAttachmentIds(message: IChatOfficeMessage): string[]
    {
        return (message.attachments ?? [])
            .map(attachment => attachment.file_id)
            .filter((fileId): fileId is string => !!fileId);
    }

    private delay(ms: number): Promise<void>
    {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    protected async ensureCurrentThread(forceRefresh = false): Promise<string>
    {
        const context = await this.threadManager.ensureThread(forceRefresh);
        this.setCurrentThreadId(context.threadId);
        return context.threadId;
    }

    protected async assignCurrentThreadId(oldThreadId: string): Promise<string>
    {
        await this.threadManager.assignThreadId(oldThreadId);
        const assignedThreadId = this.threadManager.getContext()?.threadId ?? oldThreadId;
        this.setCurrentThreadId(assignedThreadId);
        return assignedThreadId;
    }

    protected setLastAnswerMsgIdFromOfficeMessages(messages: IChatOfficeMessage[]): void
    {
        const latestAnswer = [ ...messages ].reverse().find(message =>
            message.sender === chatConstants.MESSAGE_SENDER.bot
            && !!message.msg_id
        );
        this.lastAnswerMsgId = latestAnswer?.msg_id ?? null;
    }

    protected createBotMessage(
        text: string,
        isImage: boolean,
        attachments: IR2Attachment[] = [],
        msgId?: string,
        historyItem?: IR2ChatHistoryItem,
    ): IChatOfficeMessageBot
    {
        const id = msgId ?? uuidV4();
        return {
            sender: chatConstants.MESSAGE_SENDER.bot,
            value: text,
            isImage: isImage,
            attachments,
            createdAt: historyItem?.dt ? historyItem.dt * 1_000 : Date.now(),
            model: historyItem?.model,
            tokensUsage: historyItem?.tokens_usage,
            msg_id: id,
            localId: id,
            isPending: !msgId,
        };
    }

    private write(message: IChatOfficeMessage): void
    {
        const value = this.$messages.getValue();
        value.push(message);
        this.$messages.next(value);
    }

    private async prepareDocumentContext(data: IChatActionsData | null, enabled: boolean): Promise<OfficeDocumentContext>
    {
        if (!enabled) return { attachDocument: '', documentAttachments: [] };
        // An empty attachments array also marks an already captured text-only/empty document.
        if (data?.attachDocument || data?.documentAttachments)
            return { attachDocument: data.attachDocument, documentAttachments: [...(data.documentAttachments ?? [])] };
        return readOfficeDocumentContext(this.documentOffice, this.attachmentStore);
    }

    private async prepareDocumentChatContext(enabled: boolean, supplied?: string): Promise<string>
    {
        if (!enabled) return '';
        if (supplied) return supplied;
        const messages = this.groupChat?.getMessagesValue().filter(message => !!message.text.trim()) ?? [];
        if (!messages.length) return '';
        return checkAndCutByBytes(JSON.stringify(messages), environment.limits_bytes.ATTACH_DOCUMENT);
    }

    private async currentDocumentOwner(): Promise<string | null>
    {
        try { return await this.documentOffice.getHost().getDocumentIDAsync() ?? null; }
        catch { return null; }
    }

    private buildSendRequest(text: string, dataAction: IChatActionsData | null, actions: IChatActionList, scaffold?: IScaffoldedRequest, tone = this.instructions.effectiveInstruction(), selectedSkill = this.settingService.getCurrentSkill(), includeUploads = true, accountSettings?: IResolvedUserSettings): { request: IR2SendMessageRequest; meta: IR2MessageMeta | undefined; attachments: IR2Attachment[]; }
    {
        const quickAction = this.settingService.getCurrentQuickAction();
        const skill = scaffold ? null : selectedSkill;

        if (quickAction) { this.settingService.setCurrentQuickAction(null); }

        let message = text;
        if (quickAction)
        {
            message = message.length === 0 ? quickAction.value : `${ quickAction.value }\n\n${ message }`;
        }
        else if (skill)
        {
            message = message.length === 0 ? skill.prompt! : `${ skill.prompt! }\n\n${ message }`;
        }

        // The group chat travels as an attachment, which on its own says nothing
        // about what it is. Lead with a note so the model knows the document's
        // group conversation is there and can be used as context.
        if (dataAction?.documentChat)
        {
            const groupChatNote = this.translate.instant('GROUP_CHAT.CONTEXT_ATTACHED_PROMPT') as string;
            message = message.length === 0 ? groupChatNote : `${ groupChatNote }\n\n${ message }`;
        }

        const settingOriginal = this.settingService.getSettingOriginal<IOfficeChatSetting>();
        const setting = this.settingService.getSetting();
        // What the running skill pins for itself, already resolved by the API. Every
        // field left undefined keeps whatever the chat itself is set to.
        const skillOverrides = this.skillOverrides(skill);
        const confirmed = scaffold?.accountSettings ?? accountSettings;
        const modelApiName = scaffold?.model || skillOverrides.model || confirmed?.resolvedChatModel?.apiName || setting.options?.model || settingOriginal.model;

        const model = this.aiModelsService.getModelByApiName(modelApiName) || this.aiModelsService.getDefaultModelForFeature('CHAT');

        if (!model)
        {
            throw new Error(CHAT_ERROR_NAME.MODEL_NOT_FOUND);
        }

        let webSearch = confirmed ? !!confirmed.webSearchEnabled : this.settingService.getWebSearch();
        const attachments: IR2Attachment[] = [];

        if (dataAction)
        {
            // Chat history keeps AttachDocument transport semantics; its meta label below selects the chat icon.

            if (dataAction.selection) { attachments.push({ type: R2AttachmentType.Selection, mime_type: 'text/plain', name: CHAT_ACTION_NAME.selection, data: dataAction.selection }); }
            if (dataAction.clipboard) { attachments.push({ type: R2AttachmentType.Selection, mime_type: 'text/plain', name: CHAT_ACTION_NAME.clipboard, data: dataAction.clipboard }); }
            for (const documentText of [dataAction.attachDocument, dataAction.documentChat])
                if (documentText) attachments.push({ type: R2AttachmentType.Selection, mime_type: 'text/plain', name: CHAT_ACTION_NAME.attachDocument, data: documentText });
            attachments.push(...(dataAction.documentAttachments ?? []));
        }

        // A skill pinning web search on or off outranks the chat's own toggle.
        const pinnedWebSearch = scaffold?.useWebSearch ?? skillOverrides.webSearch;
        if (pinnedWebSearch !== undefined) { webSearch = pinnedWebSearch; }

        // Uploaded file attachments from the composer (type File, carrying file_id).
        const uploadedAttachments = includeUploads && scaffold?.includeComposerAttachments !== false
            ? this.attachmentStore.readyAttachments() : [];
        attachments.push(...uploadedAttachments);

        const answerLength = scaffold?.answerLength ?? skillOverrides.answerLength;
        const lengthInstruction = answerLength?.instruction
            ?? confirmed?.answerLength?.instruction
            ?? setting.options?.answerLengthInstruction
            ?? '';
        const instruction = composeInstruction(lengthInstruction, tone);

        const imageModel = scaffold?.imgModel
            || skillOverrides.imageModel
            || this.settingService.getImageModel()
            || this.aiModelsService.getDefaultModelForFeature('TEXT_TO_IMAGE')?.apiName;

        // A skill's explicit history choice wins over the composer's toggle.
        // An inherited choice uses the toggle, even if the planner provides a wider window.
        // Later queued steps may still read answers from this same skill run.
        const queuedContextCount = scaffold?.contextMsgsCount;
        const pinnedContextCount = queuedContextCount ?? skillOverrides.contextMsgsCount;
        const historyEnabled = (scaffold?.skillHistoryEnabled
            ?? skill?.historyEnabled
            ?? (confirmed ? confirmed.messageHistoryId !== null : actions?.[ CHAT_ACTION_NAME.history ]?.value)
            ?? true)
            && pinnedContextCount !== 0;
        const historyContextCount = historyEnabled
            ? pinnedContextCount ?? (confirmed ? confirmed.messageHistory?.messageContextCount ?? 0 : setting.options?.messageHistoryContextCount)
            : scaffold?.queueStepIndex !== undefined
                ? Math.min(queuedContextCount ?? 0, scaffold.queueStepIndex * 2)
                : 0;

        // Same meta shape r2-chat-web sends: a quick action or skill tags the
        // message with its type/name, the host context (selection / clipboard /
        // document) is recorded as attachments, and the user's original text is
        // preserved. Built whenever any of these are present.
        const attachmentLabels: ContextAttachment[] = [];
        if (dataAction)
        {
            if (dataAction.selection) { attachmentLabels.push({ label: CHAT_ACTION_NAME.selection, content: dataAction.selection }); }
            if (dataAction.clipboard) { attachmentLabels.push({ label: CHAT_ACTION_NAME.clipboard, content: dataAction.clipboard }); }
            if (dataAction.attachDocument) { attachmentLabels.push({ label: CHAT_ACTION_NAME.attachDocument, content: dataAction.attachDocument }); }
            if (dataAction.documentChat) { attachmentLabels.push({ label: CHAT_ATTACHMENT_LABEL.chatDocument, content: dataAction.documentChat }); }
        }

        const meta: IR2MessageMeta | undefined = { history: historyEnabled };
        if (quickAction || skill || attachmentLabels.length > 0)
        {
            meta.original_text = text;
            if (quickAction)
            {
                meta.type = R2MessageMetaType.QuickAction;
                meta.name = quickAction.title;
            }
            else if (skill)
            {
                meta.type = R2MessageMetaType.Skill;
                meta.name = skill.title;
            }
            if (attachmentLabels.length > 0)
            {
                meta.attachmentLabels = attachmentLabels;
            }
        }

        if (scaffold?.meta)
        {
            Object.assign(meta, scaffold.meta);
        }

        const request: IR2SendMessageRequest = {
            copilot_id: this.subscriptionService.getActiveCopilot()?.id ?? '',
            // The API rejects an empty message even when a file is attached. Keep
            // the user-facing message empty, but send one ASCII space on this
            // attachment-only transport path.
            text: message.trim().length === 0 && attachments.length > 0 ? ' ' : message,
            model: model.apiName,
            instruction,
            ...(answerLength?.outputTokenLimit ?? confirmed?.answerLength?.outputTokenLimit ?? setting.options?.answerLengthOutputLimit) !== undefined
                ? { max_bytes_output: answerLength?.outputTokenLimit ?? confirmed?.answerLength?.outputTokenLimit ?? setting.options!.answerLengthOutputLimit }
                : {},
            ...(this.threadId ? { thread_id: this.threadId } : {}),
            ...(imageModel ? { img_model: imageModel } : {}),
            ...(webSearch ? { use_web_search: true } : {}),
            ...(attachments.length > 0 ? { attachments } : {}),
            ...(meta ? { meta } : {}),
            ...(historyContextCount !== undefined ? { context_msgs_count: historyContextCount } : {}),
            environment: {
                time: getLocalTimeWithTimezone(),
            },
        };

        return { request, meta, attachments: [ ...attachments ] };
    }

    /**
     * What one skill pins for a send, in the shape the request needs. The API resolves
     * its chat model from the Intelligence preset and provider, so the pinned `modelId`
     * is only a fallback for skills saved before it did.
     */
    private skillOverrides(skill: IUserSkill | null):
        {
            model?: string;
            imageModel?: string;
            webSearch?: boolean;
            contextMsgsCount?: number;
            answerLength?: IAnswerLength;
        }
    {
        if (!skill) { return {}; }

        const resolved = (skill as IResolvedUserSkill).resolvedChatModel;

        return {
            model: resolved?.apiName
                ?? skill.model?.apiName
                ?? this.aiModelsService.getModelById(skill.modelId)?.apiName,
            imageModel: skill.imageModel?.apiName
                ?? this.aiModelsService.getModelById(skill.imageModelId)?.apiName,
            webSearch: skill.webSearchEnabled ?? undefined,
            contextMsgsCount: skill.historyEnabled === false
                ? 0
                : skill.messageHistory?.messageContextCount,
            answerLength: skill.answerLength,
        };
    }

    private parseError(e: unknown): string
    {
        if (e instanceof HttpErrorResponse && e.status === 413)
        {
            return CHAT_ERROR_NAME.PAYLOAD_TOO_LARGE;
        }

        const apiErrorName = this.resolveApiErrorName(e);
        if (apiErrorName)
        {
            return apiErrorName;
        }

        if (e instanceof Error)
        {
            return e.message || 'Unknown error';
        }

        return describeError(e) || 'Unknown error';
    }

    private resolveApiErrorName(error: unknown): string | null
    {
        const apiResult = this.extractApiResult(error);
        if (apiResult === null)
        {
            return null;
        }

        return this.mapApiResultToErrorName(apiResult);
    }

    private extractApiResult(error: unknown): number | null
    {
        if (!error || typeof error !== 'object')
        {
            return null;
        }

        const candidate = error as {
            error?: {
                details?: { result?: unknown; };
            };
        };

        const apiResult = candidate.error?.details?.result;
        return typeof apiResult === 'number' ? apiResult : null;
    }

    private mapApiResultToErrorName(result: number | null | undefined): string | null
    {
        switch (result)
        {
            case R2SendMessageResult.FailServiceMaintenance:
                return CHAT_ERROR_NAME.SERVICE_MAINTENANCE;
            case R2SendMessageResult.FailInsufficientBalance:
                return CHAT_ERROR_NAME.INSUFFICIENT_TOKENS;
            case R2SendMessageResult.FailAccessDenied:
            case R2SendMessageResult.FailServiceTemporaryNotValid:
            case R2SendMessageResult.FailInvalidParameters:
                return CHAT_ERROR_NAME.UNKNOWN_ERROR;
            default:
                return null;
        }
    }
}
